/**
 * "Khách vào" — khách vãng lai đứng ở quầy (N4, docs/plans/pv2-n4-tho.md).
 *
 * Chạm: (1) chọn dịch vụ → máy chủ gợi ý kỹ thuật viên theo LƯỢT XOAY VÒNG (ít lượt nhất hôm nay; hoà
 * thì người rảnh lâu nhất) → (2) "Bắt đầu". Đổi người khác được (máy chủ ghi `walkin.override`). Tên / số
 * khách không bắt buộc (gõ số thì nhận ra khách cũ). Lịch vào bảng ở trạng thái "Đã tới".
 */
import { UserRound, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
  type Api,
  ApiFailure,
  type CustomerHit,
  chuanHoaSdt,
  gioPhut,
  type ServiceOption,
  tenDichVu,
  tienVnd,
} from './api.ts'
import { useKhoaIdem } from './GoiVi.tsx'
import { T } from './text/vi.ts'
import { ThongBao, To } from './ui.tsx'

type GoiY = Awaited<ReturnType<Api['khachVao']['goiY']>>

export function ToKhachVao({
  api,
  branchId,
  services,
  onDong,
  onXong,
  choNhapSdt = true,
}: {
  api: Api
  branchId: string
  services: ServiceOption[]
  onDong: () => void
  onXong: (tenTho: string) => void
  /**
   * ADR-078: thợ tự nhận khách chỉ ghi TÊN — gõ số là khớp hồ sơ cũ (tra khách + tài sản của khách), việc của quầy
   * (soát bảo mật GĐ4 B1; máy chủ cũng chặn).
   */
  choNhapSdt?: boolean
}) {
  const [dv, setDv] = useState<ServiceOption | null>(null)
  const [gy, setGy] = useState<GoiY | null>(null)
  const [tho, setTho] = useState<string | null>(null)
  const [moKhach, setMoKhach] = useState(false)
  const [ten, setTen] = useState('')
  const [sdt, setSdt] = useState('')
  const [dang, setDang] = useState(false)
  const [loi, setLoi] = useState<string | null>(null)
  /**
   * MỘT khoá chống lặp cho một lựa chọn: rớt mạng sau khi máy chủ đã ghi → bấm lại gửi đúng khoá cũ →
   * máy chủ trả lại lịch cũ, không tạo lịch thứ hai. Đổi dịch vụ / người / khách = yêu cầu khác → khoá mới.
   */
  const [khoa, lamMoiKhoa] = useKhoaIdem()
  const [lanTai, setLanTai] = useState(0)

  // biome-ignore lint/correctness/useExhaustiveDependencies: `lanTai` = tải lại gợi ý sau một lần lỗi.
  useEffect(() => {
    if (dv === null) return
    let con = true
    setGy(null)
    setLoi(null)
    api.khachVao
      .goiY(branchId, [dv.id])
      .then((r) => {
        if (!con) return
        setGy(r)
        setTho(r.goiY)
      })
      .catch((e: unknown) => {
        if (con) setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
      })
    return () => {
      con = false
    }
  }, [api, branchId, dv, lanTai])

  // Đổi dịch vụ / người / thông tin khách = một yêu cầu KHÁC → khoá mới (không thì máy chủ báo "cùng
  // khoá, khác nội dung").
  // biome-ignore lint/correctness/useExhaustiveDependencies: chạy lại đúng khi lựa chọn đổi.
  useEffect(() => lamMoiKhoa(), [dv?.id, tho, sdt, ten])

  const sdtChuan = sdt.trim() === '' ? null : chuanHoaSdt(sdt)
  const sdtSai = sdt.trim() !== '' && sdtChuan === null

  // RETRO bảo mật 10: số gõ trùng hồ sơ có sẵn thì máy chủ gắn khách vào hồ sơ đó — nói TRƯỚC khi bấm
  // "Bắt đầu" là hồ sơ của ai, để quầy gõ nhầm một số thì thấy ngay (không lặng lẽ gắn nhầm người).
  const [khachCu, setKhachCu] = useState<CustomerHit | null>(null)
  useEffect(() => {
    if (sdtChuan === null) {
      setKhachCu(null)
      return
    }
    let con = true
    void api
      .findCustomer(sdtChuan)
      .then((r) => {
        if (con) setKhachCu(r.customers[0] ?? null)
      })
      .catch(() => {
        /* tìm khách là tiện lợi, lỗi ở đây không chặn việc nhận khách */
      })
    return () => {
      con = false
    }
  }, [sdtChuan, api])

  async function batDau() {
    if (dv === null || tho === null || sdtSai) return
    setDang(true)
    setLoi(null)
    try {
      await api.khachVao.tao(
        {
          branchId,
          variantIds: [dv.id],
          staffId: tho,
          goiYDaThay: gy?.goiY ?? null,
          ...(ten.trim() === '' ? {} : { tenKhach: ten.trim() }),
          ...(sdtChuan === null ? {} : { sdt: sdtChuan }),
        },
        khoa,
      )
      onXong(gy?.ungVien.find((u) => u.staffId === tho)?.ten ?? '')
    } catch (e) {
      const ma = e instanceof ApiFailure ? e.code : ''
      // Mất mạng: GIỮ khoá (bấm lại = đúng yêu cầu cũ). Máy chủ từ chối: tải lại gợi ý mới nhất.
      if (ma !== 'OFFLINE') {
        lamMoiKhoa()
        setLanTai((n) => n + 1)
      }
      setLoi(e instanceof ApiFailure ? e.messageVi : T.serverFailed)
    } finally {
      setDang(false)
    }
  }

  return (
    <To nhan={T.walkInTitle} onDong={onDong}>
      <header className="to-dau">
        <h2>{T.walkInTitle}</h2>
        <button
          type="button"
          className="nut nut-nhat nut-icon"
          aria-label={T.close}
          onClick={onDong}
        >
          <X size={22} />
        </button>
      </header>
      <div className="to-than">
        {loi !== null && <ThongBao loai="loi">{loi}</ThongBao>}
        <p className="nhan">{T.walkInService}</p>
        {/* Chọn xong thì thu danh sách lại: gợi ý người làm phải hiện ngay, không nằm dưới 15 dịch vụ. */}
        {/* Lưới đều (ui-critic v21): nút rộng bằng nhau, tên trái giá phải, bỏ đuôi "— Chuẩn". */}
        <div className={dv === null ? 'luoi-dv' : 'hang-dv-chon'}>
          {(dv === null ? services : [dv]).map((s) => (
            <button
              key={s.id}
              type="button"
              className={`chip chip-dv ${dv?.id === s.id ? 'dang-chon' : ''}`}
              aria-pressed={dv?.id === s.id}
              onClick={() => setDv(s)}
            >
              <span>{tenDichVu(s.label)}</span>
              <b>{tienVnd(s.priceVnd)}</b>
            </button>
          ))}
          {dv !== null && (
            <button
              type="button"
              className="nut nut-nhat"
              onClick={() => {
                setDv(null)
                setGy(null)
                setTho(null)
              }}
            >
              {T.walkInChangeService}
            </button>
          )}
        </div>

        {dv !== null && gy === null && loi === null && <p className="chu-phu">{T.loading}</p>}
        {gy !== null && gy.startMin === null && <p className="chu-phu">{T.walkInNobody}</p>}
        {gy !== null && gy.startMin !== null && (
          <>
            <p className="nhan">{T.walkInStaff(gioPhut(gy.startMin))}</p>
            <div className="luoi-dv mot-cot">
              {gy.ungVien.map((u) => (
                <button
                  key={u.staffId}
                  type="button"
                  className={`chip chip-dv ${tho === u.staffId ? 'dang-chon' : ''}`}
                  aria-pressed={tho === u.staffId}
                  onClick={() => setTho(u.staffId)}
                >
                  <span>
                    {u.ten}
                    {u.staffId === gy.goiY && (
                      <span className="nhan-goi-y">{T.walkInSuggested}</span>
                    )}
                  </span>
                  <span className="chu-phu">{T.walkInTurns(u.luot)}</span>
                </button>
              ))}
            </div>
            {tho !== null && tho !== gy.goiY && <p className="chu-phu">{T.walkInOverride}</p>}

            {moKhach ? (
              <div className="cot-nhap">
                {choNhapSdt && (
                  <>
                    <label className="nhan" htmlFor="kv-sdt">
                      {T.walkInPhone}
                    </label>
                    <input
                      id="kv-sdt"
                      className="o-nhap"
                      inputMode="tel"
                      value={sdt}
                      onChange={(e) => setSdt(e.target.value)}
                    />
                  </>
                )}
                {sdtSai && <p className="chu-loi">{T.phoneInvalid}</p>}
                {khachCu !== null && (
                  <div className="the-khach-cu">
                    <UserRound size={20} />
                    <div>
                      <b>{khachCu.fullName}</b>
                      <span>{T.walkInMatched}</span>
                    </div>
                  </div>
                )}
                {/* Số đã khớp hồ sơ cũ: tên lấy từ hồ sơ — ô tên trống bên cạnh chỉ gây hỏi (ui-critic v24). */}
                {khachCu === null && (
                  <>
                    <label className="nhan" htmlFor="kv-ten">
                      {T.walkInName2}
                    </label>
                    <input
                      id="kv-ten"
                      className="o-nhap"
                      value={ten}
                      maxLength={120}
                      onChange={(e) => setTen(e.target.value)}
                    />
                  </>
                )}
              </div>
            ) : (
              <button type="button" className="nut nut-nhat" onClick={() => setMoKhach(true)}>
                {T.walkInAddCustomer}
              </button>
            )}
          </>
        )}
      </div>
      <footer className="to-chan">
        <button
          type="button"
          className="nut nut-chinh nut-rong"
          disabled={dang || tho === null || gy?.startMin == null || sdtSai}
          onClick={() => void batDau()}
        >
          {dang ? T.saving : T.walkInStart}
        </button>
      </footer>
    </To>
  )
}
